import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../../app/appUtils";
import AppButton from "../../../components/ui/AppButton";
import AppInputField from "../../../components/ui/AppInputField";
import { Spinner } from "../../../components/ui/common";
import { Modal } from "../../../components/ui/Modal";
import SuccessView from "../../../components/ui/SuccessView";
import { useToast } from "../../../hooks/useToast";
import { SystemPlan } from "../@types";
import {
  useCreateSystemPlanMutation,
  useUpdateSystemPlanMutation,
} from "../subscriptionApiSlice";

export const planFormSchema = z.object({
  name: z.string().nonempty("Plan name is required"),
  description: z.string().nonempty("Description is required"),
  features: z.array(z.string()).min(1, "At least one feature is required"),
  monthlyPrice: z.number().min(0, "Price must be greater than or equal to 0"),
  quarterlyPrice: z.number().min(0, "Price must be greater than or equal to 0"),
  yearlyPrice: z.number().min(0, "Price must be greater than or equal to 0"),
  maxVehicles: z.number().min(1, "Max vehicles must be greater than 0"),
  currency: z.string().nonempty("Currency is required"),
  isActive: z.boolean(),
});

export type PlanFormData = z.infer<typeof planFormSchema>;

interface PlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan?: SystemPlan | null;
  mode?: "add" | "edit";
}

const PlanConfigModal: React.FC<PlanModalProps> = ({
  isOpen,
  onClose,
  plan = null,
  mode = plan ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [featuresInput, setFeaturesInput] = useState("");
  const toast = useToast();
  const isEditMode = mode === "edit" && plan;

  const [
    createPlan,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useCreateSystemPlanMutation();

  const [
    updatePlan,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateSystemPlanMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<PlanFormData>({
    resolver: zodResolver(planFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      features: [],
      monthlyPrice: 0,
      quarterlyPrice: 0,
      yearlyPrice: 0,
      maxVehicles: 0,
      currency: "USD",
      isActive: true,
    },
  });

  // Populate form with plan data when editing
  useEffect(() => {
    if (isEditMode && plan) {
      reset({
        name: plan.name || "",
        description: plan.description || "",
        features: plan.featuresList || [],
        monthlyPrice: plan.monthlyPrice || 0,
        quarterlyPrice: plan.quarterlyPrice || 0,
        yearlyPrice: plan.yearlyPrice || 0,
        maxVehicles: plan.maxVehicles || 0,
        currency: plan.currency || "Naira",
        isActive: plan.isActive ?? true,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        name: "",
        description: "",
        features: [],
        monthlyPrice: 0,
        quarterlyPrice: 0,
        yearlyPrice: 0,
        maxVehicles: 0,
        currency: "USD",
        isActive: true,
      });
      setFeaturesInput("");
    }
  }, [isEditMode, plan, reset]);

  const handleFeaturesChange = (value: string) => {
    setFeaturesInput(value);
    const featuresArray = value
      .split(",")
      .map((f) => f.trim())
      .filter((f) => f.length > 0);
    setValue("features", featuresArray, { shouldValidate: true });
  };

  const handleAddFeature = () => {
    const trimmedFeature = featuresInput.trim();
    if (trimmedFeature) {
      const currentFeatures = watch("features") || [];
      setValue("features", [...currentFeatures, trimmedFeature], {
        shouldValidate: true,
      });
      setFeaturesInput("");
    }
  };

  const handleRemoveFeature = (index: number) => {
    const currentFeatures = watch("features") || [];
    const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
    setValue("features", updatedFeatures, { shouldValidate: true });
  };

  const onSubmit = async (data: PlanFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    // if (isEditMode && !isDirty) {
    //   toast.warning({
    //     title: "No Changes",
    //     message: "No changes were made to update.",
    //   });
    //   return;
    // }

    setIsSubmitting(true);

    try {
      const payload = {
        name: data.name,
        description: data.description,
        featuresList: data.features,
        monthlyPrice: data.monthlyPrice,
        quarterlyPrice: data.quarterlyPrice,
        yearlyPrice: data.yearlyPrice,
        maxVehicles: data.maxVehicles,
        currency: data.currency,
        isActive: data.isActive,
      };

      if (isEditMode && plan?.id) {
        await updatePlan({
          id: plan.id,
          ...payload,
        }).unwrap();
      } else {
        await createPlan(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} plan. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Plan ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} plan. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    setFeaturesInput("");
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Plan has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : `${isEditMode ? "Edit" : "Add"} plan`;
  const submitButtonText = isEditMode
    ? "Update system plan"
    : "+ Add system plan";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white pb-8"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <AppInputField
                  label="Plan name"
                  type="text"
                  error={errors.name?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter plan name"
                />
              )}
            />

            <Controller
              control={control}
              name="currency"
              render={({ field }) => (
                <AppInputField
                  label="Currency"
                  type="text"
                  error={errors.currency?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="e.g., USD, EUR"
                />
              )}
            />
          </div>

          <Controller
            control={control}
            name="description"
            render={({ field }) => (
              <AppInputField
                label="Description"
                type="text"
                error={errors.description?.message}
                {...field}
                className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                placeholder="Enter plan description"
              />
            )}
          />

          <div>
            <label className="block text-sm font-medium mb-1">Features</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={featuresInput}
                onChange={(e) => setFeaturesInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddFeature();
                  }
                }}
                className="flex-1 bg-[#F6F6F6] border-[#F6F6F6] h-[48px] px-3 rounded-md"
                placeholder="Enter a feature"
              />
              <button
                type="button"
                onClick={handleAddFeature}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors h-[48px] flex items-center gap-2"
              >
                <span>+</span>
                <span>Add</span>
              </button>
            </div>
            {watch("features").length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {watch("features").map((feature, index) => (
                  <div
                    key={index}
                    className="bg-[#F6F6F6] px-3 py-2 rounded-md flex items-center gap-2"
                  >
                    <span className="text-sm">{feature}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFeature(index)}
                      className="text-red-500 hover:text-red-700 font-bold"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {errors.features?.message && (
              <p className="text-red-500 text-sm mt-1">
                {errors.features.message}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Controller
                control={control}
                name="monthlyPrice"
                render={({ field }) => (
                  <AppInputField
                    label="Monthly price per vehicle"
                    type="number"
                    error={errors.monthlyPrice?.message}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter price"
                  />
                )}
              />

              <Controller
                control={control}
                name="maxVehicles"
                render={({ field }) => (
                  <AppInputField
                    label="Max vehicles"
                    type="number"
                    error={errors.maxVehicles?.message}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter max vehicles"
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Controller
                control={control}
                name="quarterlyPrice"
                render={({ field }) => (
                  <AppInputField
                    label="Quarterly price per vehicle"
                    type="number"
                    error={errors.quarterlyPrice?.message}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter price"
                  />
                )}
              />

              <Controller
                control={control}
                name="yearlyPrice"
                render={({ field }) => (
                  <AppInputField
                    label="Yearly price per vehicle"
                    type="number"
                    error={errors.yearlyPrice?.message}
                    {...field}
                    onChange={(e) => field.onChange(e.target.valueAsNumber)}
                    className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                    placeholder="Enter price"
                  />
                )}
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Controller
              control={control}
              name="isActive"
              render={({ field }) => (
                <input
                  type="checkbox"
                  checked={field.value}
                  onChange={field.onChange}
                  className="w-4 h-4"
                />
              )}
            />
            <label className="text-sm font-medium">Active</label>
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? <Spinner /> : submitButtonText}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default PlanConfigModal;
